import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { take, timeout } from 'rxjs/operators';
import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { API_URL } from '../constants/api-urls';

interface TelemetryConfig {
    connection_string: string;
    sampling_percentage: number;
}

// AB#85946: real-user telemetry. The connection string is environment-owned and served by
// /config/telemetry; an empty string means telemetry stays fully disabled, so this service is
// safe to call unconditionally at boot. Failures are swallowed: telemetry must never break the app.
//
// The SDK is loaded lazily (loadSdk) only when an environment enables telemetry: the static import
// added ~70KB compressed to the initial bundle that disabled environments still paid for. The
// import above is type-only and erased at compile time.
@Injectable()
export class TelemetryService {

    // Upper bound on how long init() may keep the caller waiting. AppInit awaits init() so the
    // earliest requests (auth validate and friends) are already instrumented and appear in the
    // startup waterfall; past this budget the app boots anyway and bootstrap finishes in the
    // background, losing only the requests that raced past it.
    public static readonly BOOT_BUDGET_MS = 1500;

    private appInsights: ApplicationInsights | null = null;

    private ready: Promise<void> | null = null;

    constructor(private http: HttpClient) {}

    // Always resolves, never rejects, and never later than BOOT_BUDGET_MS: fail-open by contract.
    // Idempotent: one readiness promise, at most one SDK instance and one initial page view.
    public init(): Promise<void> {
        return this.ready ??= new Promise<void>((resolve) => {
            let settled = false;
            const done = () => {
                if (!settled) {
                    settled = true;
                    clearTimeout(budget);
                    resolve();
                }
            };
            const budget = setTimeout(done, TelemetryService.BOOT_BUDGET_MS);

            let sawResponse = false;

            this.http.get<{error: boolean, data: TelemetryConfig}>(`${API_URL}/config/telemetry`).pipe(timeout(10000), take(1)).subscribe({
                next: (response) => {
                    // bootstrap never throws and never rejects; done() runs on every path so an
                    // SDK failure inside the next handler can never surface as an unhandled error.
                    sawResponse = true;
                    this.bootstrap(response).then(done, done);
                },
                error: done,
                // Only an EMPTY completion settles here: after a next, complete fires while
                // bootstrap is still attaching instrumentation and must not release boot early.
                complete: () => {
                    if (!sawResponse) {
                        done();
                    }
                },
            });
        });
    }

    protected async bootstrap(response: {error: boolean, data: TelemetryConfig}): Promise<void> {
        try {
            const config = response && response.data;

            if (!config || !config.connection_string) {
                return;
            }

            // The SDK validator rejects 0 and falls back to 100% sampling after initialization,
            // so 0 must mean "off" before a client ever exists. Non-finite garbage falls back to
            // the documented default instead of slipping past the <= 0 guard (NaN <= 0 is false).
            const rawSampling = config.sampling_percentage ?? 100;
            const samplingPercentage = Number.isFinite(rawSampling) ? rawSampling : 100;
            if (samplingPercentage <= 0) {
                return;
            }

            this.appInsights = await this.createClient({ ...config, sampling_percentage: samplingPercentage });
            this.appInsights.loadAppInsights();
            this.appInsights.trackPageView();
        } catch {
            this.appInsights = null;
        }
    }

    protected async createClient(config: TelemetryConfig): Promise<ApplicationInsights> {
        const sdk = await this.loadSdk();

        return new sdk.ApplicationInsights({
            config: {
                connectionString: config.connection_string,
                enableAutoRouteTracking: true,
                samplingPercentage: config.sampling_percentage,
                // Approved scope is page views + dependency timings only: no SDK cookies, no
                // window.onerror hook, no telemetry buffered into browser session storage.
                disableCookiesUsage: true,
                disableExceptionTracking: true,
                enableSessionStorageBuffer: false,
                excludeRequestFromAutoTrackingPatterns: [/config\/telemetry/],
            },
        });
    }

    protected loadSdk(): Promise<typeof import('@microsoft/applicationinsights-web')> {
        return import('@microsoft/applicationinsights-web');
    }
}
